<template>
  <v-form @submit.prevent="$emit('run')">
    <v-row dense align="center">
      <v-col v-for="p in params" :key="p.name" cols="12" :sm="p.type === 'daterange' ? 12 : 6" :md="p.type === 'daterange' ? 6 : 3">
        <!-- date range with presets -->
        <div v-if="p.type === 'daterange'" class="d-flex align-center">
          <v-text-field
            dense outlined hide-details type="date" :label="(p.label || p.name) + ' - from'"
            :value="(value[p.name] || {}).from" @input="setRange(p, 'from', $event)" class="mr-2"
          />
          <v-text-field
            dense outlined hide-details type="date" :label="'to'"
            :value="(value[p.name] || {}).to" @input="setRange(p, 'to', $event)"
          />
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" class="ml-1" title="Presets"><v-icon>mdi-calendar-range</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="pr in presets" :key="pr.label" @click="applyPreset(p, pr)">
                <v-list-item-title>{{ pr.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <v-text-field
          v-else-if="p.type === 'date'" dense outlined hide-details type="date" :label="p.label || p.name"
          :value="value[p.name]" @input="set(p, $event)"
        />
        <v-text-field
          v-else-if="p.type === 'number'" dense outlined hide-details type="number" :label="p.label || p.name"
          :value="value[p.name]" @input="set(p, $event)"
        />
        <v-text-field
          v-else-if="p.type === 'string'" dense outlined hide-details :label="p.label || p.name"
          :value="value[p.name]" @input="set(p, $event)"
        />
        <v-checkbox
          v-else-if="p.type === 'boolean'" dense hide-details :label="p.label || p.name"
          :input-value="value[p.name]" @change="set(p, $event)"
        />
        <v-select
          v-else-if="p.type === 'select'" dense outlined hide-details clearable
          :label="p.label || p.name" :items="optionsFor(p)" item-text="label" item-value="value"
          :multiple="!!p.multiple" :small-chips="!!p.multiple" :deletable-chips="!!p.multiple"
          :value="value[p.name]" @input="set(p, $event)"
        />
      </v-col>
      <v-col v-if="!hideRun" cols="auto">
        <v-btn color="primary" type="submit" :loading="loading"><v-icon left>mdi-play</v-icon>Run</v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
const pad = (n) => String(n).padStart(2, '0')
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

// mirrors the server's date tokens (server/rb/engine/runner.js resolveDateToken)
export function resolveToken (tok, now = new Date()) {
  if (!tok) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(tok)) return tok
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (tok === 'today') return fmt(d)
  if (tok === 'yesterday') { d.setDate(d.getDate() - 1); return fmt(d) }
  if (tok === 'startOfMonth') return fmt(new Date(d.getFullYear(), d.getMonth(), 1))
  if (tok === 'endOfMonth') return fmt(new Date(d.getFullYear(), d.getMonth() + 1, 0))
  if (tok === 'startOfLastMonth') return fmt(new Date(d.getFullYear(), d.getMonth() - 1, 1))
  if (tok === 'endOfLastMonth') return fmt(new Date(d.getFullYear(), d.getMonth(), 0))
  if (tok === 'startOfYear') return fmt(new Date(d.getFullYear(), 0, 1))
  const m = /^-(\d{1,4})d$/.exec(tok)
  if (m) { d.setDate(d.getDate() - Number(m[1])); return fmt(d) }
  return ''
}

export function defaultValues (params) {
  const out = {}
  for (const p of params) {
    if (p.type === 'daterange') {
      const d = p.default && typeof p.default === 'object' ? p.default : {}
      out[p.name] = { from: resolveToken(d.from), to: resolveToken(d.to) }
    } else if (p.type === 'date') {
      out[p.name] = resolveToken(p.default) || null
    } else if (p.multiple) {
      out[p.name] = Array.isArray(p.default) ? p.default : []
    } else {
      out[p.name] = p.default === undefined ? null : p.default
    }
  }
  return out
}

export default {
  name: 'RbFilters',
  props: {
    params: { type: Array, default: () => [] },
    value: { type: Object, required: true },
    options: { type: Object, default: () => ({}) },
    loading: Boolean,
    hideRun: Boolean
  },
  data () {
    return {
      presets: [
        { label: 'Today', from: 'today', to: 'today' },
        { label: 'Yesterday', from: 'yesterday', to: 'yesterday' },
        { label: 'Last 7 days', from: '-6d', to: 'today' },
        { label: 'Last 30 days', from: '-29d', to: 'today' },
        { label: 'This month', from: 'startOfMonth', to: 'today' },
        { label: 'Last month', from: 'startOfLastMonth', to: 'endOfLastMonth' },
        { label: 'This year', from: 'startOfYear', to: 'today' }
      ]
    }
  },
  methods: {
    optionsFor (p) {
      return p.options && p.options.length ? p.options : (this.options[p.name] || [])
    },
    set (p, v) {
      this.$emit('input', { ...this.value, [p.name]: v === undefined ? null : v })
    },
    setRange (p, key, v) {
      this.$emit('input', { ...this.value, [p.name]: { ...(this.value[p.name] || {}), [key]: v } })
    },
    applyPreset (p, pr) {
      this.$emit('input', { ...this.value, [p.name]: { from: resolveToken(pr.from), to: resolveToken(pr.to) } })
    }
  }
}
</script>
